import React, { useEffect, useRef } from 'react';

const GOOGLE_SCRIPT_ID = 'google-identity-services';

const loadGoogleScript = () => new Promise((resolve, reject) => {
  const existingScript = document.getElementById(GOOGLE_SCRIPT_ID);
  if (existingScript) {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }

    existingScript.addEventListener('load', resolve, { once: true });
    existingScript.addEventListener('error', () => reject(new Error('Failed to load Google sign-in')), { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = GOOGLE_SCRIPT_ID;
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  script.onload = resolve;
  script.onerror = () => reject(new Error('Failed to load Google sign-in'));
  document.head.appendChild(script);
});

function GoogleAuthButton({ text = 'signin_with', onCredential, onError }) {
  const buttonRef = useRef(null);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    let cancelled = false;

    if (!clientId || !buttonRef.current) {
      return undefined;
    }

    loadGoogleScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !buttonRef.current) {
          return;
        }

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            try {
              await onCredential(response.credential);
            } catch (error) {
              onError?.(error);
            }
          },
          use_fedcm_for_prompt: true,
          auto_select: false,
        });

        buttonRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          width: 320,
          text,
        });
      })
      .catch((error) => {
        onError?.(error);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, onCredential, onError, text]);

  if (!clientId) {
    return null;
  }

  return <div className="google-auth-button" ref={buttonRef} />;
}

export default GoogleAuthButton;
